import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

// Default logo imports as fallbacks
import defaultFullLogo from "@/assets/vgv-logo.png";
import defaultIconLogo from "@/assets/vgv-logo-icon.png";

export interface BrandingSettings {
  platformName: string;
  supportEmail: string;
  supportPhone: string;
  businessAddress: string;
  fullLogoUrl: string | null;
  iconLogoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  accentColor: string;
  sidebarPreset: number;
  showLogoText: boolean;
}

const DEFAULT_BRANDING: BrandingSettings = {
  platformName: "Value Gift Vouchers",
  supportEmail: "support@valuegiftvouchers.com",
  supportPhone: "+233 XX XXX XXXX",
  businessAddress: "Accra, Ghana",
  fullLogoUrl: null,
  iconLogoUrl: null,
  faviconUrl: null,
  primaryColor: "#1d6b3f",
  accentColor: "#cd7f0f",
  sidebarPreset: 0,
  showLogoText: true,
};

interface BrandingContextType {
  branding: BrandingSettings;
  loading: boolean;
  /** Resolved full logo — uploaded URL or built-in default */
  fullLogo: string;
  /** Resolved icon logo — uploaded URL or built-in default */
  iconLogo: string;
  /** Refresh branding from DB */
  refetch: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType>({
  branding: DEFAULT_BRANDING,
  loading: true,
  fullLogo: defaultFullLogo,
  iconLogo: defaultIconLogo,
  refetch: async () => {},
});

export const useBranding = () => useContext(BrandingContext);

export const BrandingProvider = ({ children }: { children: ReactNode }) => {
  const [branding, setBranding] = useState<BrandingSettings>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  const fetchBranding = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("key, value")
        .eq("key", "branding");

      if (error) {
        console.error("Failed to fetch branding settings:", error.message);
        setLoading(false);
        return;
      }

      if (data && data.length > 0) {
        const saved = data[0].value as Record<string, unknown>;
        setBranding((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(saved).filter(([, v]) => v !== undefined)
          ),
        }));
      }
    } catch (err) {
      console.error("Branding fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const fullLogo = branding.fullLogoUrl || defaultFullLogo;
  const iconLogo = branding.iconLogoUrl || defaultIconLogo;

  // Dynamically update the document favicon when branding changes
  useEffect(() => {
    if (branding.faviconUrl) {
      let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = branding.faviconUrl;
    }
  }, [branding.faviconUrl]);

  return (
    <BrandingContext.Provider value={{ branding, loading, fullLogo, iconLogo, refetch: fetchBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};
